import { IObservableArray } from 'mobx';
import * as React from 'react';

import { DateFieldState, FormAction, FormActionProvider, TravelersState } from '@shared-ui/lodging-form';
import { useLocalization } from '@shared-ui/localization-context';

import { RoomModel } from 'stores/models';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';

import { DateInput } from '__generated__/api';
import { stringify } from 'query-string';

export type OnChangeFn = (searchCriteria: SearchCriteriaModel, field?: string) => void;
export type OnSubmitFn = (value: { formData?: Record<string, unknown>; submitUrl?: string }) => void;
export type OnErrorFn = () => void;

export function buildSubmitData(
  inputs: FormData,
  action: string,
  overrides?: SearchCriteriaModel
): { formData: Record<string, unknown>; submitUrl: string } {
  const formData: any = {};
  for (const [key, value] of inputs.entries()) {
    formData[key] = value;
  }

  if (overrides) {
    formData.startDate = overrides.startDate;
    formData.endDate = overrides.endDate;
  }

  const submitUrl = `${action}?${stringify(formData)}`;

  return { formData, submitUrl };
}

export const ActionProvider: React.FC<{
  formAction: string;
  searchCriteria: SearchCriteriaModel;
  onChange?: OnChangeFn;
  onSubmit?: OnSubmitFn;
  onError?: OnErrorFn;
}> = ({ formAction, onChange, onSubmit, onError, searchCriteria, children }) => {
  const { formatDate } = useLocalization();
  const dateFormatter = (dateInput: DateInput): string =>
    formatDate(new Date(dateInput.year, dateInput.month - 1, dateInput.day), { raw: 'yyyy-MM-dd' });
  const newCriteria = searchCriteria.clone();

  const dispatch = React.useMemo(
    () => (action: FormAction) => {
      switch (action.type) {
        case 'change':
          if (!onChange) {
            return;
          }

          if (action.field === 'dates') {
            const dates = action.value as DateFieldState;
            newCriteria.startDate = dateFormatter(dates.dates?.checkInDate);
            newCriteria.endDate = dateFormatter(dates.dates?.checkOutDate);
          }

          if (action.field === 'travelers') {
            const travelers = action.value as TravelersState;
            newCriteria.rooms = travelers.rooms.map(
              room =>
                new RoomModel({
                  adults: room.adults,
                  children: room.children,
                })
            ) as IObservableArray<RoomModel>;
          }

          onChange(newCriteria, action.field);
          break;

        case 'error':
          if (onError && typeof onError === 'function') {
            onError();
          }
          break;

        case 'submit':
          action.event.preventDefault();
          if (!onSubmit) {
            return;
          }

          onSubmit(buildSubmitData(action.data, formAction));
          break;
        default:
          break;
      }
    },
    [searchCriteria, formAction]
  );

  return <FormActionProvider value={dispatch}>{children}</FormActionProvider>;
};

// Currently supports only travelers field
export const ActionProviderDatedMobile: React.FC<{ onChange?: (event) => void }> = ({ onChange, children }) => {
  const dispatch = React.useMemo(
    () => (action: FormAction) => {
      if (!(action.type === 'change' && action.field === 'travelers')) {
        return;
      }

      const travelers = action.value as TravelersState;
      const adultTravelers = travelers.rooms
        .map(room => room.adults)
        .filter(adult => adult > 0)
        .join(',');

      const childTravelers = travelers.rooms
        .map((room, index) => room.children?.map(child => `${index + 1}_${child.age}`).join(','))
        .filter(child => child && child !== '')
        .join(',');

      onChange({
        adults: adultTravelers,
        children: childTravelers,
      });
    },
    []
  );

  return <FormActionProvider value={dispatch}>{children}</FormActionProvider>;
};
